import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import { Direction } from "@Models/Direction";
import Caret from "@Resources/images/iconsets/caret.svg?react";
import { forceMoveFlyingFocus } from "@Utils/flyingFocus";
import classNames from "classnames";
import { useRef } from "react"; // Added import statement for useRef

type ScrollButtonProps = {
  onClick: () => void;
  direction: Direction;
  isVisible: boolean;
  disabled: boolean;
};

export const ScrollButton = ({
  disabled,
  onClick,
  direction,
  isVisible,
}: ScrollButtonProps) => {
  const { t } = useTranslations();
  const keyboardNavigationRef = useRef(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    keyboardNavigationRef.current = e.key === "Tab";
  };

  const handleBlur = () => {
    if (keyboardNavigationRef.current) {
      forceMoveFlyingFocus();
      keyboardNavigationRef.current = false;
    }
  };

  return (
    <button
      data-testid={
        direction === "left" ? "scroll-button-left" : "scroll-button-right"
      }
      disabled={disabled}
      type="button"
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClick={onClick}
      className={classNames(
        "m:z-10 m:h-8 m:w-8 m:cursor-pointer m:border-none m:bg-warmgrey-900 m:text-neutral-white m:shadow-scroll-buttons m:transition-opacity m:duration-200 m:ease-in-out m:hover:shadow-active-scroll-buttons m:disabled:cursor-default m:disabled:opacity-50 m:lg:h-10 m:lg:w-10 m:dark:bg-blue-850 m:dark:disabled:bg-blue-800",
        {
          "m:rounded-r": direction === "right",
          "m:rounded-l": direction === "left",
        },
        {
          "m:opacity-100": isVisible,
          "m:opacity-0!": !isVisible,
        },
      )}>
      <span className="m:flex m:items-center m:justify-center">
        <Caret className={direction === "left" ? "m:rotate-180" : ""} />
      </span>
      <span className="m:sr-only">
        {direction === "left"
          ? t("button.a11yScrollLeft")
          : t("button.a11yScrollRight")}
      </span>
    </button>
  );
};

ScrollButton.displayName = "ScrollButton";
